/* eslint-disable react/no-danger */
import React, { useState, useEffect } from "react";
import {
  Drawer,
  IconButton,
  Badge,
  Typography,
  Divider,
  Box,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  Button,
} from "@material-ui/core";
import { ArrowBack } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import NotificationsIcon from "@material-ui/icons/Notifications";
import CloseIcon from "@material-ui/icons/Close";
import Notification from "./Notification";
import alertService from "../../services/alerts.service";
import { useAuth } from "../../contexts/AuthContext";
import NotificationDialog from "./NotificationDialog";

const useStyles = makeStyles(theme => ({
  notificationIcon: {
    padding: 5,
    marginRight: 25,
    cursor: "pointer",
    background: "#fff",
    borderRadius: 50,
    boxShadow: "0px 0px 12px black",
  },
  drawer: {
    width: 400,
    maxWidth: "90vw",
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
  drawerPaper: {
    width: 400,
    maxWidth: "90vw",
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    justifyContent: "space-between",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  notificationList: {
    padding: theme.spacing(2),
    overflowY: "auto",
    height: "calc(100vh - 112px)", // Header + Tabs height
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
    textAlign: "center",
    height: "calc(100vh - 200px)",
  },
  tabs: {
    backgroundColor: "#e9e9e9",
    borderRadius: 0,
    boxShadow: "none",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
  markAllRead: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    textAlign: "right",
    paddingRight: theme.spacing(2),
  },
}));

/**
 * @function NotificationCenter
 * @description A unified notification center that displays all notifications for the user in a drawer.
 * @returns {React.Component} The JSX element representing the notification center.
 */
export default function NotificationCenter() {
  const classes = useStyles();
  const { currentUser } = useAuth();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unseenNotifications, setUnseenNotifications] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeNotification, setActiveNotification] = useState(null);
  const [acknowledging, setAcknowledging] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    if (unseenNotifications.length > 0 && !drawerOpen) setShowDialog(true);
  }, [unseenNotifications]);

  /**
   * Fetch all notifications from the server
   */
  const fetchNotifications = async () => {
    if (!currentUser || !currentUser.id) return;

    setLoading(true);

    try {
      // Use the alertService to get alerts for the current user
      const alertsData = await alertService.getAlertsForUser(currentUser.id);

      if (alertsData && alertsData.length > 0) {
        // Filter alerts based on seen status directly from the API response
        const unseenAlerts = alertsData.filter(alert => !alert.seen);

        setNotifications(alertsData);
        setUnseenNotifications(unseenAlerts);
      } else {
        setNotifications([]);
        setUnseenNotifications([]);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle opening the drawer
   */
  const handleOpenDrawer = () => {
    setDrawerOpen(true);
    fetchNotifications();
  };

  /**
   * Handle closing the drawer
   */
  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setActiveNotification(null);
  };

  /**
   * Handles an event change.
   *
   * @param {Event} event - The triggered event.
   * @param {string} newValue - The new value from the event.
   */
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  /**
   * View a notification
   * @param {object} notification The notification to view
   */
  const handleViewNotification = notification => {
    setActiveNotification(notification);
  };

  /**
   * Mark a notification as read
   * @param {object} notification The notification to mark as read
   */
  const markAsRead = async notification => {
    if (!currentUser || !currentUser.id || !notification) return;

    setAcknowledging(true);

    try {
      // Call the API to mark the alert as read
      await alertService.markAlertRead(currentUser.id, notification.id);

      // Refresh notifications
      await fetchNotifications();
    } catch (error) {
      console.error("Error marking notification as read:", error);
    } finally {
      setAcknowledging(false);
      setActiveNotification(null);
    }
  };

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = async () => {
    if (!currentUser || !currentUser.id || unseenNotifications.length === 0) {
      return;
    }

    setAcknowledging(true);

    try {
      // Mark all alerts as read in sequence
      for (const notification of unseenNotifications) {
        // eslint-disable-next-line no-await-in-loop
        await alertService.markAlertRead(currentUser.id, notification.id);
      }

      // Refresh notifications
      await fetchNotifications();
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    } finally {
      setAcknowledging(false);
    }
  };

  // Initialize notifications on component mount
  useEffect(() => {
    if (currentUser && currentUser.id) {
      fetchNotifications();
    }
  }, [currentUser]);

  // Refresh notifications every 5 minutes if drawer is open
  useEffect(() => {
    let interval;

    if (drawerOpen) {
      interval = setInterval(() => {
        fetchNotifications();
      }, 5 * 60 * 1000); // 5 minutes
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [drawerOpen]);

  // Determine which notifications to show based on active tab
  const displayedNotifications =
    activeTab === 0 ? unseenNotifications : notifications;

  return (
    <>
      {/* Notification Icon */}
      <div className={classes.notificationIcon}>
        <IconButton onClick={handleOpenDrawer}>
          <Badge
            overlap="rectangular"
            badgeContent={unseenNotifications.length}
            color="error"
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </div>

      {/* Notification Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        classes={{
          paper: classes.drawerPaper,
        }}
        className={classes.drawer}
      >
        {/* Drawer Header */}
        <div className={classes.drawerHeader}>
          <Typography variant="h6" style={{ paddingLeft: "16px" }}>
            Notifications
          </Typography>
          <IconButton onClick={handleCloseDrawer} color="inherit">
            <CloseIcon />
          </IconButton>
        </div>

        {/* Tab Navigation */}
        <Paper className={classes.tabs}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab label={`Unread (${unseenNotifications.length})`} />
            <Tab label="All Notifications" />
          </Tabs>
        </Paper>

        {/* Mark all as read button (only show if there are unread notifications) */}
        {activeTab === 0 && unseenNotifications.length > 0 && (
          <div className={classes.markAllRead}>
            <Button
              size="small"
              color="primary"
              disabled={acknowledging}
              onClick={markAllAsRead}
            >
              Mark all as read
            </Button>
          </div>
        )}

        {/* Notification Content */}
        <div className={classes.notificationList}>
          {loading ? (
            <div className={classes.loadingContainer}>
              <CircularProgress />
            </div>
          ) : activeNotification ? (
            // Detailed Notification View
            <Box>
              <Button
                color="primary"
                startIcon={<ArrowBack />}
                onClick={() => setActiveNotification(null)}
                style={{ marginBottom: 16 }}
              >
                All Notifications
              </Button>

              <Typography variant="h6" gutterBottom>
                {activeNotification.title}
              </Typography>

              {activeNotification.image && (
                <div
                  style={{
                    width: "100%",
                    textAlign: "center",
                    margin: "16px 0",
                  }}
                >
                  <img
                    src={activeNotification.image}
                    alt={activeNotification.title}
                    style={{
                      maxWidth: "100%",
                      maxHeight: 250,
                      objectFit: "contain",
                    }}
                  />
                </div>
              )}

              <div
                dangerouslySetInnerHTML={{
                  __html: activeNotification.description,
                }}
                style={{ margin: "16px 0" }}
              />

              <Divider style={{ margin: "16px 0" }} />

              <Box textAlign="center">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => markAsRead(activeNotification)}
                  disabled={acknowledging}
                >
                  {acknowledging ? "Marking as read..." : "Mark as read"}
                </Button>
              </Box>
            </Box>
          ) : displayedNotifications.length === 0 ? (
            // Empty State
            <div className={classes.emptyState}>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No notifications
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {activeTab === 0
                  ? "You have no unread notifications"
                  : "You don't have any notifications yet"}
              </Typography>
            </div>
          ) : (
            // Notification List
            displayedNotifications.map(notification => (
              <Notification
                key={notification.id}
                notif={notification}
                viewAlert={() => handleViewNotification(notification)}
              />
            ))
          )}
        </div>
      </Drawer>
      <NotificationDialog
        open={showDialog}
        unseenAlerts={unseenNotifications}
        loadingAlerts={loading}
        closeDialog={() => {
          setShowDialog(false);
        }}
        markAsRead={markAsRead}
        acknowledgingAlert={acknowledging}
      />
    </>
  );
}
